import { useEffect, useRef, useState } from "react";

export type IntercomThemeMode = "light" | "dark";

interface IntercomSettings {
  api_base: string;
  app_id: string;
  name: string;
  email: string;
  created_at: string;
  user_hash: string;
  user_id: string;
  theme_mode?: IntercomThemeMode;
}

export interface IntercomAuthenticationSettings {
  intercomUserId: string;
  intercomUserHash: string;
  intercomUserSignedUp: string;
  intercomAppId: string;
}

type IntercomActions = {
  (arg: "reattach_activator"): void;
  (arg: "update", settings: Partial<IntercomSettings>): void;
  (arg: "showArticle", articleId: string): void;
  (arg: "showNewMessage", message?: string): void;
};

declare global {
  interface Window {
    intercomSettings: IntercomSettings;
    Intercom?: IntercomActions;
  }
}

interface Params {
  fullName: string;
  email: string;
  settings: IntercomAuthenticationSettings | undefined;
  themeMode?: IntercomThemeMode;
}

const INTERCOM_API_BASE_URL = "https://api-iam.intercom.io";

interface IntercomWidget {
  isIntercomWidgetAvailable: boolean;
}

const useIntercomWidget = ({ fullName, email, settings, themeMode }: Params): IntercomWidget => {
  const [isWidgetAvailable, setIsWidgetAvailable] = useState(() => typeof window.Intercom === "function");
  const previousThemeModeRef = useRef(themeMode);

  const appId = settings?.intercomAppId;

  useEffect(() => {
    if (!settings) {
      return;
    }

    window.intercomSettings = {
      api_base: INTERCOM_API_BASE_URL,
      app_id: settings.intercomAppId,
      name: fullName,
      email,
      created_at: settings.intercomUserSignedUp,
      user_hash: settings.intercomUserHash,
      user_id: settings.intercomUserId,
      theme_mode: themeMode,
    };
  }, [email, fullName, settings, themeMode]);

  useEffect(() => {
    if (!appId) {
      return;
    }

    if (typeof window.Intercom === "function") {
      window.Intercom("reattach_activator");
      window.Intercom("update", window.intercomSettings);
      return;
    }

    const script = document.createElement("script");
    script.type = "text/javascript";
    script.defer = true;
    script.src = `https://widget.intercom.io/widget/${appId}`;
    const handleScriptLoad = () => setIsWidgetAvailable(true);
    script.addEventListener("load", handleScriptLoad);
    document.body.appendChild(script);

    return () => {
      script.removeEventListener("load", handleScriptLoad);
      if (!window.Intercom) {
        document.body.removeChild(script);
      }
    };
  }, [appId]);

  useEffect(() => {
    if (previousThemeModeRef.current === themeMode) {
      return;
    }

    previousThemeModeRef.current = themeMode;

    if (!themeMode || typeof window.Intercom !== "function") {
      return;
    }

    window.Intercom("update", { theme_mode: themeMode });
  }, [themeMode]);

  return { isIntercomWidgetAvailable: isWidgetAvailable };
};

export default useIntercomWidget;
